import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  BarChart,
  Psychology,
} from '@mui/icons-material';
import { DoctorProfileCard } from './DoctorProfileCard';
import { VisitTimeline } from './VisitTimeline';
import { EngagementAnalytics } from './EngagementAnalytics';
import { AIInsights } from './AIInsights';
import { Doctor } from '../types';
import {
  DecodedSSID,
  DoctorListItem,
  DoctorVisitSummaryResponse,
  RegionOption,
  MonthlySummary,
} from '../types/doctorTypes';
import { doctorService } from '../services/doctorService';
import { authService } from '../services/authService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Helper to generate last 3 completed months (excluding current month)
const getLastCompletedMonths = (): { label: string; value: string }[] => {
  const now = new Date();
  let year = now.getFullYear();
  let monthIndex = now.getMonth(); // 0 = Jan

  // Start from last completed month (previous month)
  monthIndex -= 1;
  if (monthIndex < 0) {
    monthIndex = 11;
    year -= 1;
  }

  const result: { label: string; value: string }[] = [];

  for (let i = 0; i < 3; i++) {
    const d = new Date(year, monthIndex, 1);
    const label = d.toLocaleString('en-US', {
      month: 'short',
      year: 'numeric',
    }); // e.g. "Feb 2026"

    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; // e.g. "2026-02-01"

    result.push({ label, value });

    // Move to previous month
    monthIndex -= 1;
    if (monthIndex < 0) {
      monthIndex = 11;
      year -= 1;
    }
  }

  return result;
};
// Helper function to convert MonthlySummary to Doctor type
const convertMonthlySummaryToDoctor = (summary: MonthlySummary): Doctor => ({
  id: summary.Doctor_Code,
  name: summary.Doctor_Name,
  specialty: summary.Speciality,
  qualification: summary.Qualification,
  category: summary.Category,
  business_category_name: summary.Business_Category_Name,
  location: summary.Location,
  address: summary.CityOrAddress,
  phone: summary.Mobile,
  email: summary.Email,
});

const getDoctorsArray = async (companyCode: string, regionCode: string): Promise<DoctorListItem[]> => {
  const doctorsList = await doctorService.getAllDoctors(companyCode, regionCode);
  return Array.isArray(doctorsList) ? doctorsList : [];
};

const getUniqueRegions = (doctorsList: DoctorListItem[]): RegionOption[] =>
  Array.from(
    new Map(
      doctorsList.map((doctor) => [
        doctor.Region_Code,
        { Region_Code: doctor.Region_Code, Region_Name: doctor.Region_Name },
      ])
    ).values()
  );

const mergeRegionOptions = (currentRegions: RegionOption[], newRegions: RegionOption[]): RegionOption[] => {
  const currentRegionCodes = new Set(currentRegions.map((region) => region.Region_Code));
  return [...currentRegions, ...newRegions.filter((region) => !currentRegionCodes.has(region.Region_Code))];
};

const getSelectedRegionCode = (initialRegion: string, regions: RegionOption[]): string => {
  if (initialRegion && regions.some((region) => region.Region_Code === initialRegion)) {
    return initialRegion;
  }

  return regions[0]?.Region_Code ?? '';
};

const getFirstDoctorId = (doctorsList: DoctorListItem[]): string => doctorsList[0]?.Doctor_Code ?? '';

const getRegionOptionLabel = (option: RegionOption): string => {
  if (option.Region_Name) {
    return option.Region_Name;
  }

  if (option.Region_Code) {
    return option.Region_Code;
  }

  return 'Unknown Region';
};

const getDoctorOptionLabel = (option: DoctorListItem): string => {
  if (option.Doctor_Name) {
    return option.Doctor_Name;
  }

  return 'Unknown Doctor';
};

const getManagerRegionState = async (companyCode: string, initialRegion: string) => {
  try {
    const doctorsArray = await getDoctorsArray(companyCode, initialRegion);
    const regions = getUniqueRegions(doctorsArray);

    return {
      regions,
      selectedRegion: getSelectedRegionCode(initialRegion, regions),
    };
  } catch (err) {
    console.error('Failed to fetch initial doctors for region extraction:', err);
    return null;
  }
};

async function applyManagerInitialization(
  decodedSSID: DecodedSSID,
  setAvailableRegions: React.Dispatch<React.SetStateAction<RegionOption[]>>,
  setSelectedRegion: React.Dispatch<React.SetStateAction<string>>,
): Promise<void> {
  const initialRegion = decodedSSID.Region_Code ?? '';
  const managerRegionState = await getManagerRegionState(decodedSSID.Company_Code, initialRegion);
  if (!managerRegionState) {
    return;
  }
  setAvailableRegions(managerRegionState.regions);
  if (managerRegionState.selectedRegion) {
    setSelectedRegion(managerRegionState.selectedRegion);
  }
}

async function applyRegularUserInitialization(
  decodedSSID: DecodedSSID,
  selectedDoctorIdAtMount: string,
  setDoctors: React.Dispatch<React.SetStateAction<DoctorListItem[]>>,
  setSelectedDoctorId: React.Dispatch<React.SetStateAction<string>>,
): Promise<void> {
  if (!decodedSSID.Region_Code) {
    console.error('Missing Region_Code for non-manager user');
    return;
  }

  const doctorsArray = await getDoctorsArray(decodedSSID.Company_Code, decodedSSID.Region_Code);
  setDoctors(doctorsArray);

  if (doctorsArray.length > 0 && !selectedDoctorIdAtMount) {
    const firstDoctorId = getFirstDoctorId(doctorsArray);
    if (firstDoctorId) {
      setSelectedDoctorId(firstDoctorId);
    }
  }
}

async function bootstrapDashboardFromURL(
  selectedDoctorIdAtMount: string,
  setCompanyCode: React.Dispatch<React.SetStateAction<string>>,
  setIsManager: React.Dispatch<React.SetStateAction<number>>,
  setAvailableRegions: React.Dispatch<React.SetStateAction<RegionOption[]>>,
  setSelectedRegion: React.Dispatch<React.SetStateAction<string>>,
  setDoctors: React.Dispatch<React.SetStateAction<DoctorListItem[]>>,
  setSelectedDoctorId: React.Dispatch<React.SetStateAction<string>>,
): Promise<void> {
  const ssid = authService.extractSSIDFromURL();
  if (!ssid) {
    console.error('No SSID found in URL');
    return;
  }

  const decodedSSID = authService.decodeSSID(ssid);
  if (!decodedSSID?.Company_Code) {
    console.error('Failed to decode SSID or missing Company_Code');
    return;
  }

  setCompanyCode(decodedSSID.Company_Code);

  const managerStatus = await authService.getIsManager();
  setIsManager(managerStatus);

  if (managerStatus === 1) {
    await applyManagerInitialization(decodedSSID, setAvailableRegions, setSelectedRegion);
  } else {
    await applyRegularUserInitialization(decodedSSID, selectedDoctorIdAtMount, setDoctors, setSelectedDoctorId);
  }
}

// Default doctor profile shown when no data is loaded yet
const defaultDoctorProfile: Doctor = {
  id: '',
  name: 'Select a doctor to view profile',
  specialty: '',
  qualification: '',
  category: '',
  business_category_name: '',
  location: '',
  address: '',
  phone: '',
  email: '',
};

export const Dashboard: React.FC = () => {
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const [visitSummary, setVisitSummary] = useState<DoctorVisitSummaryResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [companyCode, setCompanyCode] = useState<string>('');
  const [isManager, setIsManager] = useState<number>(0);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [availableRegions, setAvailableRegions] = useState<RegionOption[]>([]);
  const [monthOptions] = useState(() => getLastCompletedMonths());
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const opts = getLastCompletedMonths();
    return opts[0]?.value ?? '';
  });

  // Convert last_visits from API to Visit format for compatibility
  const visits = visitSummary?.last_visits?.map((lastVisit) => ({
    visitId: lastVisit.DCR_Visit_Code,
    date: lastVisit.DCR_Date,
    type: 'Visit',
    product: lastVisit.Product_Name ?? '',
    rep: '',
    duration: lastVisit.Played_Time ?? 0,
    Played_Time: lastVisit.Played_Time,
    outcome: lastVisit.Business_Status,
    notes: lastVisit.Business_Remarks ?? '',
    samplesGiven: lastVisit.Samples_Given,
    literatureShared: [],
    nextAction: lastVisit.Next_Action ?? '',
    nextActionDue: '',
    competitorMentioned: null,
    objections: null,
    commitments: [],
  })) ?? [];

  useEffect(() => {
    const initialize = async () => {
      try {
        await bootstrapDashboardFromURL(
          selectedDoctorId,
          setCompanyCode,
          setIsManager,
          setAvailableRegions,
          setSelectedRegion,
          setDoctors,
          setSelectedDoctorId,
        );
      } catch (err) {
        console.error('Failed to initialize:', err);
      }
    };

    void initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; doctor list sets selection separately
  }, []);

  // Fetch doctors when region changes (for managers)
  useEffect(() => {
    const fetchDoctorsForRegion = async () => {
      if (isManager === 1 && selectedRegion && companyCode) {
        try {
          const doctorsArray = await getDoctorsArray(companyCode, selectedRegion);
          setDoctors(doctorsArray);
          setSelectedDoctorId(''); // Reset selected doctor when region changes
          
          // Update available regions with any new regions found in the doctor list
          setAvailableRegions((prev) => mergeRegionOptions(prev, getUniqueRegions(doctorsArray)));
          
          if (doctorsArray.length > 0) {
            const firstDoctorId = getFirstDoctorId(doctorsArray);
            if (firstDoctorId) {
              setSelectedDoctorId(firstDoctorId);
            }
          }
        } catch (err) {
          console.error('Failed to fetch doctors for region:', err);
          setDoctors([]);
        }
      }
    };

    fetchDoctorsForRegion();
  }, [isManager, selectedRegion, companyCode]);

  const handleDoctorChange = (doctorId: string) => {
    setSelectedDoctorId(doctorId);
  };

  // Fetch visit summary when selectedDoctorId or month changes
  useEffect(() => {
    if (companyCode && selectedDoctorId && selectedMonth) {
      const fetchSummary = async () => {
        try {
          setLoadingSummary(true);
          const summary = await doctorService.getDoctorVisitSummary(companyCode, selectedDoctorId, selectedMonth);
          setVisitSummary(summary);
        } catch (err) {
          console.error('Failed to fetch doctor visit summary:', err);
          setVisitSummary(null);
        } finally {
          setLoadingSummary(false);
        }
      };
      fetchSummary();
    }
  }, [companyCode, selectedDoctorId, selectedMonth]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const totalVisits = visitSummary?.monthly_summary?.Total_Visits ?? 0;
  const avgDuration = Math.round(visitSummary?.monthly_summary?.Avg_Play_Time_Minutes ?? 0);
  const totalSamples = visitSummary?.monthly_summary?.Samples_Given ?? 0;
  const feedbackRate = Math.round(visitSummary?.monthly_summary?.Feedback_Percentage ?? 0);

  let doctorProfileContent: React.ReactNode = <DoctorProfileCard key="default-profile" doctor={defaultDoctorProfile} />;
  if (visitSummary?.monthly_summary) {
    doctorProfileContent = (
      <DoctorProfileCard
        key={selectedDoctorId}
        doctor={convertMonthlySummaryToDoctor(visitSummary.monthly_summary)}
      />
    );
  } else if (loadingSummary) {
    doctorProfileContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  let visitTimelineContent: React.ReactNode = <VisitTimeline key={`visits-${selectedDoctorId}`} visits={visits} />;
  if (loadingSummary) {
    visitTimelineContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (visits.length === 0) {
    visitTimelineContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No visits available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F9F5EF', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 3 }}>
        {/* Header with Region and Doctor Selection */}
        <Paper sx={{ p: 2, mb: 3, boxShadow: "none", backgroundColor: 'transparent' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Month Selection */}
            <Autocomplete
              options={monthOptions}
              getOptionLabel={(option) => option.label}
              value={monthOptions.find((month) => month.value === selectedMonth)}
              onChange={(_event, newValue) => {
                if (newValue) {
                  setSelectedMonth(newValue.value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Month"
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#C16D18',
                      },
                      '&:hover fieldset': {
                        borderColor: '#C16D18',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#C16D18',
                      },
                    },
                  }}
                />
              )}
              sx={{ minWidth: 200 }}
              disableClearable
            />

            {/* Region Selection (only for managers) */}
            {isManager === 1 && (
              <Autocomplete
                options={availableRegions}
                getOptionLabel={getRegionOptionLabel}
                value={availableRegions.find((region) => region.Region_Code === selectedRegion) ?? null}
                onChange={(_event, newValue) => {
                  if (newValue) {
                    setSelectedRegion(newValue.Region_Code);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Region"
                    sx={{
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#C16D18',
                        },
                        '&:hover fieldset': {
                          borderColor: '#C16D18',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#C16D18',
                        },
                      },
                    }}
                  />
                )}
                sx={{ minWidth: 300 }}
                disabled={availableRegions.length === 0}
                noOptionsText="No regions available"
              />
            )}
            
            {/* Doctor Selection */}
            <Autocomplete
              options={doctors}
              getOptionLabel={getDoctorOptionLabel}
              value={doctors.find((doctor) => doctor.Doctor_Code === selectedDoctorId) ?? null}
              onChange={(_event, newValue) => {
                if (newValue) {
                  handleDoctorChange(newValue.Doctor_Code);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Doctor"
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#C16D18',
                      },
                      '&:hover fieldset': {
                        borderColor: '#C16D18',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#C16D18',
                      },
                    },
                  }}
                />
              )}
              sx={{ minWidth: 300 }}
              disabled={doctors.length === 0 || (isManager === 1 && !selectedRegion)}
              noOptionsText={isManager === 1 && !selectedRegion ? "Please select a region first" : "No doctors available"}
            />
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Sidebar - Doctor Profile */}
          <Grid item xs={12} md={3}>
            {doctorProfileContent}
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <BarChart sx={{ color: 'primary.main', mb: 0.3 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {totalVisits}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Visits
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {avgDuration}m
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg. Duration
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center', boxShadow: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {totalSamples}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Samples Given
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    textAlign: 'center',
                    boxShadow: 2,
                    bgcolor: '#ff9800',
                    color: 'white',
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: '#C16A18',
                      mx: 'auto',
                      mb: 1,
                    }}
                  />
                  <Typography variant="h5" fontWeight="bold">
                    {feedbackRate}%
                  </Typography>
                  <Typography variant="caption">
                    Feedback Rate
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Paper sx={{ borderRadius: 2, boxShadow: 2 ,width:"fit-content"}}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  paddingRight: 2,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                  },
                  '& .Mui-selected': {
                    color: '#ff9800',
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: '#ff9800',
                  },
                }}
              >
                <Tab
                  icon={<TimelineIcon />}
                  iconPosition="start"
                  label="Visit Timeline"
                />
                <Tab
                  icon={<BarChart />}
                  iconPosition="start"
                  label="Analytics"
                />
                <Tab
                  icon={<Psychology />}
                  iconPosition="start"
                  label="AI Insights"
                />
              </Tabs>
              </Paper>

              <Paper sx={{ pl:4 ,backgroundColor:"#fff",marginTop:"30px" }}>
                <TabPanel value={activeTab} index={0}>
                  {visitTimelineContent}
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <EngagementAnalytics key={`analytics-${selectedDoctorId}`} visits={visits} />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  <AIInsights
                    companyCode={companyCode}
                    customerCode={selectedDoctorId}
                    month={selectedMonth}
                  />
                </TabPanel>
              </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

